import { Divider } from "@msuite/ui/divider";
import { Textarea } from "@msuite/ui/form";
import type { Editor } from "@msuite/ui/libs/tiptap";
import { useTranslation } from "@msuite/ui/localization";
import type { WikiPageSchema } from "@msuite/utils/schemas";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useMentionOptions } from "@/hooks/use-mention-options";
import { useImageRenderer } from "@/hooks/use-image-renderer";
import { useVideoRenderer } from "@/hooks/use-video-renderer";

/** Props Interface */
interface WikiPageFormProps {
	/**
	 * What the reading view shows under the title — who wrote the page and when. Nothing in it
	 * is edited here, but it belongs to the page rather than to the mode, so it stays where the
	 * reader saw it.
	 */
	byline?: React.ReactNode;
	/**
	 * The element the formatting toolbar renders into — the editor keeps it in its header row.
	 * Without it the toolbar sits above the text.
	 */
	menuContainer?: HTMLElement | null;
	onEditorReady?: (editor: Editor | null) => void;
}

export function WikiPageForm({ byline, menuContainer, onEditorReady }: WikiPageFormProps) {
	/** Hooks */
	const methods = useFormContext<WikiPageSchema>();
	const { t } = useTranslation();
	const mentionsOptions = useMentionOptions();
	const videoRenderer = useVideoRenderer();
	const imageRenderer = useImageRenderer();

	/** Computed */
	const titleError = methods.formState.errors.title?.message;

	/** Render */
	return (
		<React.Fragment>
			{/* The title is typed where it is read: a bare field in the heading's own type, so
			    entering edit mode changes what the author can do, not where anything sits. */}
			<input
				{...methods.register("title")}
				type="text"
				placeholder={t("wiki.page_title_placeholder")}
				aria-invalid={!!titleError}
				className="mt-4 w-full bg-transparent text-3xl font-semibold leading-tight tracking-tight outline-none placeholder:text-primaryGray-300 dark:placeholder:text-primaryGray-600"
			/>
			{titleError && <p className="mt-1 text-xs text-red-500">{titleError}</p>}
			{byline}
			<Divider className="my-6" />
			{/* The document likewise: no input box around it. The reading view sets the text on
			    the column itself, and a border and padding here moved every line the moment editing
			    began. Only the toolbar says the page is now being written. */}
			<Textarea
				label={undefined}
				isRequired={false}
				control={methods.control}
				name="content"
				size="full"
				variant="plain"
				menuContainer={menuContainer}
				videoRenderer={videoRenderer}
				imageRenderer={imageRenderer}
				onEditorReady={onEditorReady}
				className="tiptap-document"
				{...mentionsOptions}
			/>
		</React.Fragment>
	);
}
